import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone } from "lucide-react";
import { ATTORNEYS, STAFF } from "../mock";

const PageBanner = ({ eyebrow, title, subtitle }) => (
  <section className="bg-nw-navy text-white pt-16 pb-20 md:pt-20 md:pb-24 relative overflow-hidden">
    <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: "radial-gradient(circle at 20% 20%, #b18f5f 0%, transparent 50%)",
    }} />
    <div className="container-nw relative">
      <div className="text-nw-gold-light text-xs uppercase tracking-[0.4em] mb-5">
        {eyebrow}
      </div>
      <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-tight">
        {title}
      </h1>
      <div className="h-0.5 w-20 bg-nw-gold mt-6" />
      {subtitle && (
        <p className="mt-6 text-white/80 max-w-2xl text-base md:text-lg leading-relaxed">
          {subtitle}
        </p>
      )}
    </div>
  </section>
);

const Attorneys = () => {
  const partners = ATTORNEYS.filter(
    (a) => a.title && a.title.toLowerCase().includes("partner")
  );
  const associates = ATTORNEYS.filter(
    (a) => !a.title || !a.title.toLowerCase().includes("partner")
  );

  const renderAttorneyCard = (a) => (
    <div key={a.slug} className="group">
      <Link
        to={`/attorneys/${a.slug}`}
        className="block relative overflow-hidden bg-nw-stone aspect-[4/5]"
      >
        <img
          src={a.photo}
          alt={`${a.firstName} ${a.lastName}`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-nw-navy-dark/70 via-transparent to-transparent" />
      </Link>
      <div className="mt-6">
        <div className="text-nw-gold text-xs uppercase tracking-[0.3em] mb-2">
          {a.title}
        </div>
        <Link to={`/attorneys/${a.slug}`}>
          <h3 className="font-serif-display text-3xl md:text-4xl text-nw-navy hover:text-nw-gold transition-colors">
            {a.firstName} {a.lastName}
          </h3>
        </Link>
        <div className="h-0.5 w-12 bg-nw-gold mt-4 mb-5" />
        <div className="flex flex-wrap gap-2 mb-5">
          {a.practiceAreas.slice(0, 4).map((pa) => (
            <span
              key={pa}
              className="text-xs text-nw-navy border border-nw-stone bg-nw-cream px-3 py-1.5"
            >
              {pa}
            </span>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 text-sm text-nw-navy/70 mb-6">
          <a href={`mailto:${a.email}`} className="flex items-center gap-2 hover:text-nw-gold">
            <Mail className="w-4 h-4" /> {a.email}
          </a>
          <a href={`tel:${a.phone}`} className="flex items-center gap-2 hover:text-nw-gold">
            <Phone className="w-4 h-4" /> {a.phone}
          </a>
        </div>
        <Link
          to={`/attorneys/${a.slug}`}
          className="inline-flex items-center gap-2 text-nw-navy hover:text-nw-gold text-xs font-bold uppercase tracking-[0.15em] link-underline"
        >
          View Full Profile <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );

  return (
    <div className="bg-white">
      <PageBanner
        eyebrow="The Team"
        title="Our Team"
        subtitle="Our attorneys and staff work together to serve clients across construction litigation, insurance coverage, and complex commercial matters throughout South Carolina."
      />

      <section className="py-20 md:py-24">
        <div className="container-nw">
          <div className="mb-12">
            <div className="text-nw-gold text-xs uppercase tracking-[0.35em] mb-3">
              Founding Partners
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-nw-navy">
              Our Attorneys
            </h2>
            <div className="h-0.5 w-14 bg-nw-gold mt-4" />
          </div>
          <div className="grid md:grid-cols-2 gap-10 lg:gap-14">
            {partners.map(renderAttorneyCard)}
          </div>
        </div>
      </section>

      {associates.length > 0 && (
        <section className="pb-20 md:pb-24 pt-4">
          <div className="container-nw">
            <div className="mb-12 pt-10 border-t border-nw-stone">
              <div className="text-nw-gold text-xs uppercase tracking-[0.35em] mb-3">
                Associate Attorneys
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-nw-navy">
                Working Alongside Our Partners
              </h2>
              <div className="h-0.5 w-14 bg-nw-gold mt-4" />
            </div>
            <div className="grid md:grid-cols-2 gap-10 lg:gap-14">
              {associates.map(renderAttorneyCard)}
            </div>
          </div>
        </section>
      )}

      {/* Firm Staff */}
      <section className="pb-20 md:pb-28 pt-4">
        <div className="container-nw">
          <div className="mb-12 pt-10 border-t border-nw-stone">
            <div className="text-nw-gold text-xs uppercase tracking-[0.35em] mb-3">
              Firm Staff
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-nw-navy">
              Operations & Paralegal Support
            </h2>
            <div className="h-0.5 w-14 bg-nw-gold mt-4" />
          </div>

          <div className="grid md:grid-cols-2 gap-10 lg:gap-14">
            {STAFF.map((s) => (
              <div key={s.slug} className="group">
                <Link
                  to={`/team/${s.slug}`}
                  className="block relative overflow-hidden bg-nw-stone aspect-[4/5]"
                >
                  <img
                    src={s.photo}
                    alt={`${s.firstName} ${s.lastName}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-nw-navy-dark/70 via-transparent to-transparent" />
                </Link>
                <div className="mt-6">
                  <div className="text-nw-gold text-xs uppercase tracking-[0.3em] mb-2">
                    {s.title}
                  </div>
                  <Link to={`/team/${s.slug}`}>
                    <h3 className="font-serif-display text-3xl md:text-4xl text-nw-navy hover:text-nw-gold transition-colors">
                      {s.firstName} {s.lastName}
                    </h3>
                  </Link>
                  <div className="h-0.5 w-12 bg-nw-gold mt-4 mb-5" />
                  <div className="flex flex-wrap gap-2 mb-5">
                    {s.focus.map((f) => (
                      <span
                        key={f}
                        className="text-xs text-nw-navy border border-nw-stone bg-nw-cream px-3 py-1.5"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 text-sm text-nw-navy/70 mb-6">
                    <a href={`mailto:${s.email}`} className="flex items-center gap-2 hover:text-nw-gold">
                      <Mail className="w-4 h-4" /> {s.email}
                    </a>
                    <a href={`tel:${s.phone}`} className="flex items-center gap-2 hover:text-nw-gold">
                      <Phone className="w-4 h-4" /> {s.phone}
                    </a>
                  </div>
                  <Link
                    to={`/team/${s.slug}`}
                    className="inline-flex items-center gap-2 text-nw-navy hover:text-nw-gold text-xs font-bold uppercase tracking-[0.15em] link-underline"
                  >
                    View Full Profile <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export { PageBanner };
export default Attorneys;
